import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { connect, useDispatch } from "react-redux";
import { Box, CircularProgress } from "@mui/material";

import { StyledEngineProvider } from '@mui/material/styles';
import "./style.css";

import { PreEventDetails } from "../PreEventDetails";
import Stepper from '../Stepper';

import homeImage from '../../../icons/ImageIcons/material-symbols_home-rounded.svg';
import rightarrow from '../../../icons/ImageIcons/keyboard-arrow-right.svg';

import { Group } from "../../../components/Group";
import { completeStep, FetchCustomerClipConfig, FetchGradreelType, getEventState, setEventDetails } from "../../../store/Actions/PreEventAction";
import { getLoginUser } from "../../../store/Actions/SharedAction";
import { getClientData, setClientData } from "../../../constants/Constants";

const getEventDisplayValues = (eventData, fallbackEventDateTime = null) => {
  const combinedEventDateTime = eventData?.eventDateTime
    || eventData?.eventDateTimeInUTC
    || eventData?.EventDateTime
    || eventData?.EventDateTimeInUTC
    || fallbackEventDateTime
    || null;

  if (combinedEventDateTime) {
    const parsedDate = new Date(combinedEventDateTime);
    if (!Number.isNaN(parsedDate.getTime())) {
      return {
        eventDateTime: combinedEventDateTime,
        eventDate: '',
        eventTime: '',
        dateText: parsedDate.toLocaleDateString(),
        timeText: parsedDate.toLocaleTimeString(),
      };
    }
  }

  const rawEventDate = eventData?.eventDate || eventData?.EventDate || '';
  const rawEventTime = eventData?.eventTime || eventData?.EventTime || '';

  let dateText = '';
  if (rawEventDate) {
    const parsedDate = new Date(rawEventDate);
    dateText = Number.isNaN(parsedDate.getTime()) ? rawEventDate : parsedDate.toLocaleDateString();
  }

  let timeText = '';
  if (rawEventTime) {
    const parsedTime = new Date(`1970-01-01 ${rawEventTime}`);
    timeText = Number.isNaN(parsedTime.getTime()) ? rawEventTime : parsedTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  return {
    eventDateTime: null,
    eventDate: rawEventDate,
    eventTime: rawEventTime,
    dateText,
    timeText,
  };
};


const EventOverview = ({ loading, eventDetails, loadevent, eventMaster, getEventState, FetchCustomerClipConfig, customerClipDetails, loginUserDetails, getLoginUser, setEventDetails }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [eventDisplay, setEventDisplay] = useState(() => getEventDisplayValues({}, eventDetails?.eventDateTime));
  const [isLoadEventCompleted, setIsLoadEventCompleted] = useState(false);
  const [deepLinkBootstrap, setDeepLinkBootstrap] = useState(false);

  // Deep-link bootstrap: when no eventDetails, try to get from Query + GradReelsPayload
  useEffect(() => {
    if (eventDetails) return;
    const params = new URLSearchParams(location.search);
    const queryValue = params.get('Query') || params.get('query');
    if (queryValue) {
      setDeepLinkBootstrap(true);
      getLoginUser(queryValue);
    } else {
      navigate('/PreEventDetailsDashboard');
    }
  }, [location.search]);

  // When loginUserDetails arrives with eventID from payload, set event details and client data
  useEffect(() => {
    if (!deepLinkBootstrap || eventDetails) return;
    const result = loginUserDetails?.result;
    if (result?.eventID) {
      setClientData(result);
      setEventDetails({
        clientId: result.clientID,
        eventId: result.eventID,
        eventName: result.eventName || '',
        eventDateTime: result.eventDateTime || result.eventDateTimeInUTC || null,
      });
      setDeepLinkBootstrap(false);
    } else if (loginUserDetails?.isSuccess === false) {
      setDeepLinkBootstrap(false);
      navigate('/PreEventDetailsDashboard');
    }
  }, [loginUserDetails?.isSuccess, loginUserDetails?.result?.eventID]);
  useEffect(() => {
    const eventData = eventMaster?.result?.eventData;
    const hydratedEventName = eventData?.eventName;
    const hydratedEventDisplay = getEventDisplayValues(eventData, eventDetails?.eventDateTime);

    setEventDisplay(hydratedEventDisplay);

    if (!eventDetails || (!hydratedEventName && !hydratedEventDisplay.eventDateTime && !hydratedEventDisplay.eventDate && !hydratedEventDisplay.eventTime)) {
      return;
    }

    const nextEventName = hydratedEventName || eventDetails.eventName || '';
    const nextEventDateTime = hydratedEventDisplay.eventDateTime || eventDetails.eventDateTime || null;
    const nextEventDate = hydratedEventDisplay.eventDate || eventDetails.eventDate || '';
    const nextEventTime = hydratedEventDisplay.eventTime || eventDetails.eventTime || '';

    if (
      eventDetails.eventName === nextEventName
      && eventDetails.eventDateTime === nextEventDateTime
      && eventDetails.eventDate === nextEventDate
      && eventDetails.eventTime === nextEventTime
    ) {
      return;
    }

    setEventDetails({
      ...eventDetails,
      eventName: nextEventName,
      eventDateTime: nextEventDateTime,
      eventDate: nextEventDate,
      eventTime: nextEventTime,
    });
  }, [
    eventMaster?.result?.eventData?.eventName,
    eventMaster?.result?.eventData?.eventDateTime,
    eventMaster?.result?.eventData?.eventDateTimeInUTC,
    eventMaster?.result?.eventData?.EventDateTime,
    eventMaster?.result?.eventData?.EventDateTimeInUTC,
    eventMaster?.result?.eventData?.eventDate,
    eventMaster?.result?.eventData?.EventDate,
    eventMaster?.result?.eventData?.eventTime,
    eventMaster?.result?.eventData?.EventTime,
    eventDetails?.clientId,
    eventDetails?.eventId,
    eventDetails?.eventName,
    eventDetails?.eventDateTime,
    eventDetails?.eventDate,
    eventDetails?.eventTime,
  ]);

  useEffect(() => {
    (async () => {
        let updateData;
        if (eventDetails) {
            updateData = { eventId: eventDetails.eventId, clientId: eventDetails.clientId };
            await loadevent(updateData);
            await FetchCustomerClipConfig(updateData);
            setIsLoadEventCompleted(true); // Set to true when loadevent completes
        }
    })();

    return () => {
      setIsLoadEventCompleted(true);
    };
  }, [eventDetails, loadevent]);

  useEffect(() => {
    if((eventMaster?.result?.eventData?.eventState === "Draft" && eventMaster?.result?.eventData?.eventStage === "PreEvent") || !eventMaster?.result?.isDataAvailable){
      getEventState(false);
    } else {
      getEventState(true);
    }
  },[eventMaster?.result?.eventData])

  // Updates the steps status of completed or in progress
  useEffect(() => {
    if (!isLoadEventCompleted) return;
    if(eventMaster?.result?.isDataAvailable){
    if(eventMaster?.result?.eventData?.template){
      dispatch(completeStep(1, true));
      if(eventMaster?.result?.eventData?.template === 'Standard'){
        if(eventMaster?.result?.eventData?.backGroundMusicProvided !== null &&
          eventMaster?.result?.eventData?.studentDataUploaded !== null &&
          eventMaster?.result?.eventData?.backGroundMusicProvided &&
          eventMaster?.result?.eventData?.studentDataUploaded){
          dispatch(completeStep(2, true));
        } else {
          dispatch(completeStep(2, false));
        }
        if(eventMaster?.result?.eventData?.introClipComplete){
          dispatch(completeStep(3, true));
        } else {
          dispatch(completeStep(3, false));
        }
        if(eventMaster?.result?.eventData?.exitClipComplete){
          dispatch(completeStep(4, true));
        } else {
          dispatch(completeStep(4, false));
        }
        if(eventMaster?.result?.eventData?.introClipPublished !== null &&
          eventMaster?.result?.eventData?.exitClipPublished !== null &&
          eventMaster?.result?.eventData?.introClipPublished &&
          eventMaster?.result?.eventData?.exitClipPublished){
          dispatch(completeStep(5, true));
        } else {
          dispatch(completeStep(5, false));
        }
      } else {
        if(eventMaster?.result?.eventData?.backGroundMusicProvided !== null &&
          eventMaster?.result?.eventData?.studentDataUploaded !== null &&
          eventMaster?.result?.eventData?.backGroundMusicProvided &&
          eventMaster?.result?.eventData?.studentDataUploaded){
          dispatch(completeStep(2, true));
        } else {
          dispatch(completeStep(2, false));
        }
        if(eventMaster?.result?.eventData?.introClipComplete){
          dispatch(completeStep(3, true));
        } else {
          dispatch(completeStep(3, false));
        }
        if(customerClipDetails && customerClipDetails?.result?.isDataAvailable){
          dispatch(completeStep(4, true));
        } else {
          dispatch(completeStep(4, false));
        }
        if(eventMaster?.result?.eventData?.exitClipComplete){
          dispatch(completeStep(5, true));
        } else {
          dispatch(completeStep(5, false));
        }
        if(eventMaster?.result?.eventData?.introClipPublished !== null &&
          eventMaster?.result?.eventData?.exitClipPublished !== null &&
          eventMaster?.result?.eventData?.introClipPublished &&
          eventMaster?.result?.eventData?.exitClipPublished){
          dispatch(completeStep(6, true));
        } else {
          dispatch(completeStep(6, false));
        }
      }
    }
  }
  }, [eventMaster?.result?.eventData, customerClipDetails])

  const displayEventName = eventMaster?.result?.eventData?.eventName || eventDetails?.eventName || '';
  const date = eventDisplay.dateText;
  const time = eventDisplay.timeText;

  return (
    <div className="event-overview">
      <div className="div-2">
       <PreEventDetails />
        <div className="frame-19">
          <div className="frame-20">
            <div className="section-title">
              <div className="frame-21">
                <div className="page-title">
                  <div className="text-wrapper-3">Event Details</div>
                </div>
                <div className="breadcrum">
                  <img
                    className="material-symbols"
                    alt="Material symbols"
                    src={homeImage}
                  />
                  <img
                    className="keyboard-arrow-right"
                    alt="Keyboard arrow right"
                    src={rightarrow}
                  />
                  <div className="text-wrapper-4">Events</div>
                  <img
                    className="keyboard-arrow-right"
                    alt="Keyboard arrow right"
                    src={rightarrow}
                  />
                  <div className="text-wrapper-5">Event Details</div>
                </div>
              </div>
            </div>
            <div className="frame-22">
              <div className="frame-23">
                            <StyledEngineProvider injectFirst>
                    <Stepper gradReelType={eventMaster?.result?.eventData?.template}/>
                  </StyledEngineProvider>
              </div>
              <div className="frame-27">
                <div className="frame-28">
                  <div className="frame-29">
                    <div className="text-wrapper-6">Event details</div>
                  </div>
                 
                  <div className="frame-30">
                    <div className="frame-31">
                     
                      <Group
                        className="group-37146"
                        divClassName="group-37146-instance"
                        divClassNameOverride="group-2"
                        frameClassName="group-3"
                        text={displayEventName}
                        text1="Event Name"
                        visible={false}
                        visible1={false}
                      />
                      <Group
                        className="group-37146"
                        divClassName="group-37146-instance"
                        divClassNameOverride="group-2"
                        frameClassName="group-3"
                        text={eventDetails?.eventId}
                        text1="Event ID"
                        visible={false}
                        visible1={false}
                      />
                    </div>
                    
                    <div className="frame-31">
                      <Group
                        className="group-37146"
                        divClassName="group-37146-instance"
                        divClassNameOverride="group-2"
                        frameClassName="group-3"
                        text={date}
                        text1="Event Date"
                        visible={false}
                        visible1={false}
                      />
                      <Group
                        className="group-37146"
                        divClassName="group-37146-instance"
                        divClassNameOverride="group-2"
                        frameClassName="group-3"
                        text={time}
                        text1="Event Time"
                        visible={false}
                        visible1={false}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
         <Link to="/SelectTemplate" className="button-text">
            <div className="primary-BTN-wrapper">
              <div className="primary-BTN">
                Continue
              </div>
            </div>
          </Link>
        </div>
        {(loading || deepLinkBootstrap) && (
            <div className="loader-container">
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '10px' }}>
                <CircularProgress />
              </Box>
            </div>
          )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    loading: state.preEvent.loading,
    eventDetails: state.preEvent.eventDetails,
    eventMaster: state.preEvent.gradreelType,
    customerClipDetails: state.preEvent.customerClipDetails,
    loginUserDetails: state.shared.loginUserDetails,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    loadevent: (eventData) => dispatch(FetchGradreelType(eventData)),
    getEventState: (eventState) => dispatch(getEventState(eventState)),
    FetchCustomerClipConfig: (eventData) => dispatch(FetchCustomerClipConfig(eventData)),
    getLoginUser: (query) => dispatch(getLoginUser(query)),
    setEventDetails: (details) => dispatch(setEventDetails(details)),
  };
};

const ConnectedEventOverview = connect(mapStateToProps, mapDispatchToProps)(EventOverview);

export { ConnectedEventOverview as EventOverview };